import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Flame, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      setError('Por favor completa todos los campos obligatorios');
      return false;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }

    if (formData.password !== formData.password_confirmation) {
      setError('Las contraseñas no coinciden');
      return false;
    }

    if (!acceptedTerms) {
      setError('Debes aceptar los términos y condiciones');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    const result = await register(formData);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || 'Error al crear la cuenta');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="pt-8 pb-4 px-4">
        <Link to="/" className="flex items-center justify-center gap-2">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Flame size={28} className="text-white" />
          </div>
          <span className="font-display font-bold text-2xl text-[#1B263B]">
            SammyGas
          </span>
        </Link>
      </div>

      {/* Form Container */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-display font-bold text-[#1B263B] mb-2">
              Crear cuenta
            </h1>
            <p className="text-[#7A756F]">
              Regístrate para hacer pedidos más rápido
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-700 animate-fadeIn">
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <Input
              label="Nombre completo"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="Juan Pérez"
              required
              disabled={loading}
            />

            <Input
              label="Correo electrónico"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
              disabled={loading}
            />

            <Input
              label="Teléfono"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+56 9 1234 5678"
              required
              disabled={loading}
            />

            <div className="relative">
              <Input
                label="Contraseña"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-[38px] text-[#9A9590] hover:text-[#1B263B] transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            <Input
              label="Confirmar contraseña"
              name="password_confirmation"
              type={showPassword ? 'text' : 'password'}
              value={formData.password_confirmation}
              onChange={handleChange}
              placeholder="••••••••"
              required
              disabled={loading}
            />

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input 
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-[#EFEDEA] text-primary focus:ring-primary"
              />
              <span className="text-sm text-[#7A756F]">
                Acepto los{' '}
                <Link to="/terminos" className="text-primary hover:underline">
                  términos y condiciones
                </Link>
                {' '}y la{' '}
                <Link to="/privacidad" className="text-primary hover:underline">
                  política de privacidad
                </Link>
              </span>
            </label>

            <Button
              type="submit"
              size="lg"
              fullWidth
              disabled={loading}
            >
              {loading ? (
                <LoadingSpinner size="sm" color="white" />
              ) : (
                'Crear Cuenta'
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-[#7A756F]">
              ¿Ya tienes una cuenta?{' '}
              <Link 
                to="/login" 
                className="text-primary font-semibold hover:underline"
              >
                Iniciar sesión
              </Link>
            </p>
          </div>

          {/* Benefits */}
          <div className="mt-8 pt-8 border-t border-[#EFEDEA]">
            <p className="text-sm font-semibold text-[#1B263B] mb-4 text-center">
              Beneficios de registrarte
            </p>
            <div className="space-y-3">
              {[
                'Historial de pedidos',
                'Pedidos más rápidos',
                'Ofertas exclusivas',
                'Guarda tus direcciones',
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-[#5A564F]">
                  <CheckCircle size={16} className="text-[#38B000]" />
                  {benefit}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
